/**
 * Zod schema for `WorkspaceView` — validates DB rows on read AND admin
 * submissions on write. Mirrors the guardrail philosophy of `themePackSchema`:
 *
 *   - Unknown nav-item / widget ids are silently DROPPED with a console.warn,
 *     so a row authored against a newer registry on another deployment never
 *     hard-crashes the runtime; it just hides the unknown items.
 *   - `landingPath` must point to a registered nav destination so the admin
 *     can't accidentally redirect users to an arbitrary URL.
 *   - `copyPresetId` must be a real preset id (or null).
 *   - `pageDefaults` keys must be in the allowlist.
 */
import { z } from "zod";
import { COPY_PRESETS, type CopyPresetId } from "@/theme/copy/presets";
import {
  ALLOWED_LANDING_PATHS,
  PAGE_DEFAULT_PATHS,
  isNavItemId,
  isWidgetId,
} from "@/workspace/registry";

const COPY_PRESET_IDS = Object.keys(COPY_PRESETS) as CopyPresetId[];

const navItemSchema = z.object({
  id: z.string(),
  order: z.number().int().min(0).max(999),
  label: z.string().trim().max(60).optional(),
});

const widgetSchema = z.object({
  id: z.string(),
  order: z.number().int().min(0).max(999),
});

export const workspaceViewSchema = z
  .object({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(280).nullable(),
    roleName: z.string().trim().min(1).max(80).nullable(),
    isDefault: z.boolean(),
    landingPath: z.string(),
    navItems: z.array(navItemSchema),
    dashboardWidgets: z.array(widgetSchema),
    pageDefaults: z.record(z.string(), z.string().max(400)),
    copyPresetId: z.string().nullable(),
    createdBy: z.string().uuid(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .transform((row) => {
    // Drop unknown nav/widget ids defensively.
    const navItems = row.navItems.filter((n) => {
      if (isNavItemId(n.id)) return true;
      console.warn(`[workspace] dropped unknown nav item: ${n.id}`);
      return false;
    });
    const dashboardWidgets = row.dashboardWidgets.filter((w) => {
      if (isWidgetId(w.id)) return true;
      console.warn(`[workspace] dropped unknown widget: ${w.id}`);
      return false;
    });

    // Drop page-default keys not in the allowlist.
    const pageDefaults: Record<string, string> = {};
    for (const [k, v] of Object.entries(row.pageDefaults)) {
      if (PAGE_DEFAULT_PATHS.includes(k)) pageDefaults[k] = v;
      else console.warn(`[workspace] dropped page default for unknown route: ${k}`);
    }

    // Coerce landing path: must be either an allowed nav URL or fall back to
    // "/getting-started" (always safe).
    const landingPath = ALLOWED_LANDING_PATHS.includes(row.landingPath)
      ? row.landingPath
      : "/getting-started";
    if (landingPath !== row.landingPath) {
      console.warn(`[workspace] coerced landing path "${row.landingPath}" -> "${landingPath}"`);
    }

    // Coerce copy preset to a known id, or null.
    const copyPresetId =
      row.copyPresetId && (COPY_PRESET_IDS as string[]).includes(row.copyPresetId)
        ? (row.copyPresetId as CopyPresetId)
        : null;

    return {
      ...row,
      navItems,
      dashboardWidgets,
      pageDefaults,
      landingPath,
      copyPresetId,
    };
  });

/** Schema for the editor draft (no DB-managed columns). */
export const workspaceViewDraftSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(80),
  description: z.string().trim().max(280).nullable(),
  roleName: z.string().trim().min(1, "Bind to a role").max(80).nullable(),
  isDefault: z.boolean(),
  landingPath: z
    .string()
    .refine((v) => ALLOWED_LANDING_PATHS.includes(v), "Pick a registered destination"),
  navItems: z.array(navItemSchema),
  dashboardWidgets: z.array(widgetSchema),
  pageDefaults: z.record(
    z.string().refine((k) => PAGE_DEFAULT_PATHS.includes(k), "Unsupported route"),
    z.string().max(400),
  ),
  copyPresetId: z
    .string()
    .nullable()
    .refine(
      (v) => v === null || (COPY_PRESET_IDS as string[]).includes(v),
      "Unknown copy preset",
    ),
});
